import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getStoredToken, clearStoredToken } from '../utils/tokenStorage';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth headers, cache-busting, and logging
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getStoredToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Prevent browser caching of GET requests (ensures fresh data after updates)
    if (config.method === 'get' && config.headers) {
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';
    }
    // Debug logging for search requests
    if (config.url?.includes('search')) {
      console.log('[API Client] Request:', config.method?.toUpperCase(), config.url);
      console.log('[API Client] Request data:', JSON.stringify(config.data)?.substring(0, 500));
    }
    return config;
  },
  (error: AxiosError) => {
    console.error('[API Client] Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and logging
apiClient.interceptors.response.use(
  (response) => {
    // Debug logging for search responses
    if (response.config.url?.includes('search')) {
      console.log('[API Client] Response:', response.status, response.config.url);
      console.log('[API Client] Response data stat:', response.data?.stat);
      console.log('[API Client] Response molecules count:', response.data?.molecules?.length);
    }
    return response;
  },
  (error: AxiosError) => {
    // Suppress error logging for health-check endpoints (expected to fail when services aren't deployed)
    const url = error.config?.url || '';
    if (!url.endsWith('/health')) {
      console.error('[API Client] Response error:', error.response?.status, error.message);
    }
    if (error.response?.status === 401) {
      clearStoredToken();
      window.location.href = '/login';
    }
    if (error.response?.status === 403) {
      const data = error.response?.data as Record<string, unknown>;
      const detail = data?.detail;
      const message = typeof detail === 'string' ? detail : 'Insufficient permissions';
      console.warn(`[403 Forbidden] ${error.config?.url}: ${message}`);
    }
    return Promise.reject(error);
  }
);

export default apiClient;
